/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module MessageItemsModule
 */

import { Component, Type } from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import { IDNSConfig } from 'generated-types';
import { DNSConfig } from 'object-types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';

import * as l10n from './dns-config.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description DNSConfig ConfigItem class.
 *
 * @author Nisar Nadaf
 */
export class DNSConfigConfigItem extends withFullModalMixin(
    withEditChildMessageItemMixin<IDNSConfig, typeof MessageItem>(MessageItem),
)<IDNSConfig> {
    public static ngDependencies = [
        'l10nService',
    ];

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: DNSConfig,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);
        this.l10nService = this.getNgDependency('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Return configured domain name.
     */
    public get domainName(): string {
        return this.config.domain_name;
    }

    /**
     * Method used to import lazy-loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { GslbSubdomainModalComponent } = await import(
            /* webpackChunkName: "gslb-subdomain-modal" */
            'ng/lazy-loaded-components/modals/gslb-subdomain-modal/gslb-subdomain-modal.component'
        );

        return GslbSubdomainModalComponent as Type<Component>;
    }

    /** @override */
    public getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.dnsConfigModalBreadcrumbTitle);
    }
}
